import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot
} from '@angular/router';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ApiError,
  ApiResponse,
  AuthApiService,
  HttpStatusCodesEnum,
} from '@spa/api';
import { LanguageService } from './language.service';
import { PortalPaths, AuthenticationPaths } from './paths';
import { Helper } from './classes';


@Injectable()
export class AuthGuardService implements CanActivateChild, CanActivate {
  constructor(
    private authenticationService: AuthApiService,
    private languageService: LanguageService,
    private router: Router) {
  }

  private isPublicUrl(state: RouterStateSnapshot): boolean {
    const language = this.languageService.getCurrentLanguage();
    const dashboardPath = `/${language}/${PortalPaths.root}/${PortalPaths.dashboard.path}`;
    const productPath = `/${language}/${PortalPaths.root}/${PortalPaths.product.path}`;

    const publicUrls = Helper.isPrototypePortal()
      ? [dashboardPath, productPath]
      : [];

    const foundUrl = publicUrls.find(url => state.url.startsWith(url));

    return publicUrls.indexOf(foundUrl) >= 0;
  }

  private mapHandler(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    redirectToHome: boolean,
    response: ApiResponse<ApiError[] | boolean>,
  ): boolean {
    const isPublicUrl = this.isPublicUrl(state);

    if (isPublicUrl) {
      return true;
    }

    const language = this.languageService.getCurrentLanguage();
    const userHomePath = Helper.getPortalHomepage(language, true);
    const anonHomePath = Helper.getPortalHomepage(language, false);

    if (response.status !== HttpStatusCodesEnum.Ok) {
      if (response.status === HttpStatusCodesEnum.InternalServerError) {
        if (Helper.isPrototypePortal() && userHomePath === state.url) {
          this.router
            .navigate([anonHomePath])
            .finally();

          return true;
        }

        this.redirectToLogin();
      }
      else {
        const messages = response.messages.join(' ');
        const url = `/${language}/error`;

        if (state.url !== url) {
          this.router
            .navigate([url], {
              state: {
                statuses: response.status,
                messages: `Authentication error. ${messages}`,
              },
            })
            .finally();
        }
      }

      return false;
    }

    const userCheck = response.data as boolean;

    if (!userCheck) {
      if (Helper.isPrototypePortal() && userHomePath === state.url) {
        this.router
          .navigate([anonHomePath])
          .finally();

        return true;
      }

      this.redirectToLogin();

      return false;
    }

    if (redirectToHome && state && state.url !== userHomePath) {
      this.router.navigate([userHomePath])
        .finally();
    }

    return true;
  }

  private redirectToLogin(): void {
    const language = this.languageService.getCurrentLanguage();

    this.router
      .navigate([`/${language}`, AuthenticationPaths.root, AuthenticationPaths.children.login.path])
      .finally();
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authenticationService
      .isUserAuthenticated()
      .pipe(
        map(this.mapHandler.bind(this, route, state, false)),
      );
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    return this.canActivate(route, state);
  }
}
